import React, {FC, ReactNode} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {darken} from "polished";

type ButtonType = "button" | "submit" | "reset";
type ButtonSize = "lg" | "sm";
type ButtonVariant = "default" | "success" | "danger" | "primary" | "link" | "dark" | "bright" | "gethome" | "white";

export interface IButtonProps {
    children?: ReactNode;
    type?: ButtonType;
    size?: ButtonSize;
    variant?: ButtonVariant;
    className?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    inverted?: boolean | undefined;
    disabled?: boolean;
    dataTestId?: string;
    fullWidth?: boolean;
    icon?: JSX.Element;
    isLoading?: boolean;
    loaderIcon?: JSX.Element;

    //button can be used as="div" or link (pass 'href' prop or specify as="a")
    href?: string;
    target?: string;
    title?: string;
    as?: "a" | "button" | "div";
}

interface IThemeProps {
    size: ButtonSize;
    variant?: ButtonVariant;
    inverted: boolean | undefined;
    fullWidth?: boolean;
    as?: string;
    href?: string;
    title?: string;
    target?: string;
    icon?: JSX.Element;
    isLoading?: boolean;
}

export const Button: FC<IButtonProps> = (props: IButtonProps) => {
    return (
        <ButtonBase
            as={props.as || props.href ? "a" : "button"}
            data-testid={props.dataTestId}
            size={props.size as ButtonSize}
            className={props.className}
            onClick={props.onClick}
            variant={props.variant}
            inverted={props.inverted}
            disabled={props.disabled || props.isLoading}
            isLoading={props.isLoading}
            fullWidth={props.fullWidth}
            href={props.href}
            target={props.href && props.target}
            title={props.title}
            type={props.type}
            icon={props.icon}
        >
            <ButtonContentWrapper isLoading={props.isLoading} fullWidth={props.fullWidth} isIcon={!!props.icon}>
                {props.icon}
                {props.children}
            </ButtonContentWrapper>

            {props.isLoading && props.loaderIcon && <div css={[loaderIconWrapper]}>{props.loaderIcon}</div>}
        </ButtonBase>
    );
};

Button.defaultProps = {
    type: "button",
    variant: "default"
};

const ButtonBase = styled.button<IThemeProps>`
    // prevent Safari from showing rounded corners and gradient backgrounds.
    -webkit-appearance: none !important;

    position: relative;
    display: inline-flex;
    margin-bottom: 0;
    ${(props) =>
        props.as !== "button" &&
        css`
            text-align: center;
        `}
    justify-content: center;
    white-space: nowrap;
    align-items: center;
    touch-action: manipulation;
    cursor: pointer;
    background-image: none;
    border-width: 1px;
    border-style: solid;
    height: ${(props) => props.theme?.buttons?.btn_height_base ?? "4.2rem"};
    font-weight: ${(props) => props.theme?.buttons?.btn_font_weight ?? 500};
    color: ${(props) => (props.inverted ? props.theme?.buttons?.btn_default_bg ?? "#ccc" : props.theme?.buttons?.btn_default_color ?? "#fff")};
    background-color: ${(props) => (props.inverted ? "transparent" : props.theme?.buttons?.btn_default_bg ?? "#ccc")};
    border-color: ${(props) => props.theme?.buttons?.btn_default_border ?? "#ccc"};
    user-select: none;
    padding: ${(props) => props.theme?.padding?.padding_base_vertical ?? ".9rem"} ${(props) => props.theme?.padding?.padding_base_horizontal ?? "1.8rem"};
    font-size: ${(props) => props.theme?.fonts?.font_size_base ?? "1.4rem"};
    line-height: ${(props) => props.theme?.fonts?.line_height_base ?? 1.428571429};
    border-radius: ${(props) => props.theme?.buttons?.btn_border_radius_base ?? ".4rem"};
    transition: ${(props) => props.theme?.buttons?.btn_transition ?? "background-color .2s ease-in-out, color .2s ease-in-out, border-color .2s ease-in-out"};

    > svg {
        transition: ${(props) => props.theme?.buttons?.btn_transition_icon ?? "fill .2s ease-in-out"};
    }

    ${(props) =>
        props.fullWidth &&
        css`
            width: 100%;
        `}

    &:hover,
    &:focus,
    &.focus,
    &:active,
    &.active,
    &:active:hover,
    &.active:hover,
    &:active:focus,
    &.active:focus,
    &:active.focus,
    &.active.focus {
        background-color: ${(props) => (props.inverted ? "transparent" : props.theme?.buttons?.btn_default_bg_hover ?? darken(0.1, "#ccc"))};
        color: ${(props) =>
            props.inverted ? props.theme?.buttons?.btn_default_color_hover ?? darken(0.3, "#fff") : props.theme?.buttons?.btn_default_color ?? "#fff"};
        border-color: ${(props) => props.theme?.buttons?.btn_default_border_hover ?? darken(0.1, "#ccc")};
        outline: 0 !important;
        text-decoration: none;
    }

    &.disabled,
    &[disabled] {
        cursor: ${(props) => props.theme?.buttons?.btn_cursor_disabled ?? "not-allowed"};
        opacity: 0.65;

        &:hover,
        &:focus,
        &.focus {
            background-color: ${(props) => (props.inverted ? "transparent" : props.theme?.buttons?.btn_default_bg ?? "#ccc")};
            border-color: ${(props) => props.theme?.buttons?.btn_default_border ?? "#ccc"};
        }
    }

    ${(props) =>
        props.icon &&
        css`
            svg {
                fill: ${props.inverted
                    ? props.theme?.buttons?.btn_default_color_hover ?? darken(0.3, "#fff")
                    : props.theme?.buttons?.btn_default_color ?? "#fff"};
                margin-right: 1rem;
            }
        `}

    ${(props) =>
        props.size === "lg" &&
        css`
            height: ${props.theme?.buttons?.btn_height_large ?? "5rem"};
            padding: ${props.theme?.padding?.padding_large_vertical ?? "1.2rem"} ${props.theme?.padding?.padding_large_horizontal ?? "2.4rem"};
            font-size: ${props.theme?.fonts?.font_size_large ?? "1.4rem"};
            line-height: ${props.theme?.fonts?.line_height_large ?? 1.3333333};
            border-radius: ${props.theme?.buttons?.btn_border_radius_large ?? ".5rem"};
        `};

    ${(props) =>
        props.size === "sm" &&
        css`
            height: ${props.theme?.buttons?.btn_height_small ?? "3.4rem"};
            padding: ${props.theme?.padding?.padding_small_vertical ?? ".6rem"} ${props.theme?.padding?.padding_small_horizontal ?? "1.2rem"};
            font-size: ${props.theme?.fonts?.font_size_small ?? "1.2rem"};
            line-height: ${props.theme?.fonts?.line_height_small ?? 1.428571429};
            border-radius: ${props.theme?.buttons?.btn_border_radius_small ?? ".3rem"};
        `};

    ${(props) =>
        props.variant === "primary" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_primary_bg ?? "#2979ff" : props.theme?.buttons?.btn_primary_color ?? "#fff"};
            background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_primary_bg ?? "#2979ff"};
            border-color: ${props.theme?.buttons?.btn_primary_border ?? "#2979ff"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_primary_bg_hover ?? darken(0.1, "#2979ff")};
                color: ${props.inverted
                    ? props.theme?.buttons?.btn_primary_color_hover ?? darken(0.1, "#2979ff")
                    : props.theme?.buttons?.btn_primary_color ?? "#fff"};
                border-color: ${props.theme?.buttons?.btn_primary_border_hover ?? darken(0.1, "#2979ff")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_primary_bg ?? "#2979ff"};
                    border-color: ${props.theme?.buttons?.btn_primary_border ?? "#2979ff"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_primary_bg ?? "#2979ff" : props.theme?.buttons?.btn_primary_color ?? "#fff"};
            }
        `};

    ${(props) =>
        props.variant === "success" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_success_bg ?? "#45bc83" : props.theme?.buttons?.btn_success_color ?? "#fff"};
            background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_success_bg ?? "#45bc83"};
            border-color: ${props.theme?.buttons?.btn_success_border ?? "#45bc83"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_success_bg_hover ?? darken(0.1, "#45bc83")};
                color: ${props.inverted
                    ? props.theme?.buttons?.btn_success_color_hover ?? darken(0.1, "#45bc83")
                    : props.theme?.buttons?.btn_success_color ?? "#fff"};
                border-color: ${props.theme?.buttons?.btn_success_border_hover ?? darken(0.1, "#45bc83")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_success_bg ?? "#45bc83"};
                    border-color: ${props.theme?.buttons?.btn_success_border ?? "#45bc83"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_success_bg ?? "#45bc83" : props.theme?.buttons?.btn_success_color ?? "#fff"};
            }
        `};

    ${(props) =>
        props.variant === "danger" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_danger_bg ?? "#ff5a5f" : props.theme?.buttons?.btn_danger_color ?? "#fff"};
            background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_danger_bg ?? "#ff5a5f"};
            border-color: ${props.theme?.buttons?.btn_danger_border ?? "#ff5a5f"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_danger_bg_hover ?? darken(0.1, "#ff5a5f")};
                color: ${props.inverted
                    ? props.theme?.buttons?.btn_danger_color_hover ?? darken(0.1, "#ff5a5f")
                    : props.theme?.buttons?.btn_danger_color ?? "#fff"};
                border-color: ${props.theme?.buttons?.btn_danger_border_hover ?? darken(0.1, "#ff5a5f")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_danger_bg ?? "#ff5a5f"};
                    border-color: ${props.theme?.buttons?.btn_danger_border ?? "#ff5a5f"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_danger_bg ?? "#ff5a5f" : props.theme?.buttons?.btn_danger_color ?? "#fff"};
            }
        `};

    ${(props) =>
        props.variant === "dark" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_dark_color_inverted ?? "#43444d" : props.theme?.buttons?.btn_dark_color ?? "#fff"};
            background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_dark_bg ?? "#909090"};
            border-color: ${props.theme?.buttons?.btn_dark_border ?? "#909090"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_dark_bg_hover ?? darken(0.1, "#909090")};
                color: ${props.inverted
                    ? props.theme?.buttons?.btn_dark_color_inverted_hover ?? darken(0.1, "#43444d")
                    : props.theme?.buttons?.btn_dark_color ?? "#fff"};
                border-color: ${props.theme?.buttons?.btn_dark_border_hover ?? darken(0.1, "#909090")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_dark_bg ?? "#909090"};
                    border-color: ${props.theme?.buttons?.btn_dark_border ?? "#909090"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_dark_color_inverted ?? "#43444d" : props.theme?.buttons?.btn_dark_color ?? "#fff"};
            }
        `};

    ${(props) =>
        props.variant === "bright" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_bright_color_inverted ?? "#fff" : props.theme?.buttons?.btn_bright_color ?? "#43444d"};
            background-color: ${props.inverted ? props.theme?.buttons?.btn_dark_bg ?? "#909090" : props.theme?.buttons?.btn_bright_bg ?? "#f9f9f9"};
            border-color: ${props.theme?.buttons?.btn_bright_border ?? "#f9f9f9"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted
                    ? props.theme.buttons.btn_bright_color ?? "#43444d"
                    : props.theme?.buttons?.btn_bright_bg_hover ?? darken(0.05, "#f9f9f9")};
                color: ${props.inverted
                    ? props.theme?.buttons?.btn_bright_color_inverted_hover ?? "#fff"
                    : props.theme?.buttons?.btn_bright_color ?? "#43444d"};
                border-color: ${props.theme?.buttons?.btn_bright_border_hover ?? darken(0.05, "#f9f9f9")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_bright_bg ?? "#f9f9f9"};
                    border-color: ${props.theme?.buttons?.btn_bright_border ?? "#f9f9f9"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_bright_color_inverted ?? "#fff" : props.theme?.buttons?.btn_bright_color ?? "#43444d"};
            }
        `};

    ${(props) =>
        props.variant === "gethome" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_gethome_bg ?? "#9069c0" : props.theme?.buttons?.btn_gethome_color ?? "#fff"};
            background-color: ${props.inverted ? "#fff" : props.theme?.buttons?.btn_gethome_bg ?? "#9069c0"};
            border-color: ${props.theme?.buttons?.btn_gethome_border ?? "#9069c0"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted ? "#fff" : props.theme?.buttons?.btn_gethome_bg_hover ?? darken(0.1, "#9069c0")};
                color: ${props.inverted
                    ? props.theme?.buttons?.btn_gethome_color_hover ?? darken(0.1, "#9069c0")
                    : props.theme?.buttons?.btn_gethome_color ?? "#fff"};
                border-color: ${props.theme?.buttons?.btn_gethome_border_hover ?? darken(0.1, "#9069c0")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_gethome_bg ?? "#9069c0"};
                    border-color: ${props.theme?.buttons?.btn_gethome_border ?? "#9069c0"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_gethome_bg ?? "#9069c0" : props.theme?.buttons?.btn_gethome_color ?? "#fff"};
            }
        `};

    ${(props) =>
        props.variant === "white" &&
        css`
            color: ${props.inverted ? props.theme?.buttons?.btn_white_bg ?? "#fff" : props.theme?.buttons?.btn_white_color ?? "#fff"};
            background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_white_bg ?? "#fff"};
            border-color: ${props.theme?.buttons?.btn_white_border ?? "#fff"};

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_white_bg_hover ?? darken(0.1, "#fff")};
                color: ${props.inverted ? props.theme?.buttons?.btn_white_color_hover ?? darken(0.1, "#fff") : props.theme?.buttons?.btn_white_color ?? "#fff"};
                border-color: ${props.theme?.buttons?.btn_white_border_hover ?? darken(0.1, "#fff")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background-color: ${props.inverted ? "transparent" : props.theme?.buttons?.btn_white_bg ?? "#fff"};
                    border-color: ${props.theme?.buttons?.btn_white_border ?? "#fff"};
                }
            }

            svg {
                fill: ${props.inverted ? props.theme?.buttons?.btn_white_bg ?? "#fff" : props.theme?.buttons?.btn_white_color ?? "#fff"};
            }
        `};

    ${(props) =>
        props.variant === "link" &&
        css`
            color: ${props.theme?.buttons?.btn_link_color ?? "#45bc83"};
            background: transparent;
            border-style: none;

            &:hover,
            &:focus,
            &.focus,
            &:active,
            &.active,
            &:active:hover,
            &.active:hover,
            &:active:focus,
            &.active:focus,
            &:active.focus,
            &.active.focus {
                background: none;
                color: ${props.theme?.buttons?.btn_link_color_hover ?? darken(0.1, "#45bc83")};
            }

            &.disabled,
            &[disabled] {
                &:hover,
                &:focus,
                &.focus {
                    background: transparent;
                }
            }

            svg {
                fill: ${props.theme?.buttons?.btn_link_color ?? "#45bc83"};
            }
        `};
`;

const ButtonContentWrapper = styled.span<{isLoading?: boolean; fullWidth?: boolean; isIcon?: boolean}>`
    opacity: 1;
    transition-property: opacity;
    transition-duration: 0.15s;
    transition-timing-function: ease-in-out;

    ${(props) =>
        props.isLoading &&
        css`
            opacity: 0.2;
        `};

    ${(props) =>
        props.fullWidth &&
        css`
            width: 100%;
        `}

    ${(props) =>
        props.isIcon &&
        css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
        `}
`;

const loaderIconWrapper = css`
    position: absolute;
    width: 100%;
    height: 100%;

    display: flex;
    justify-content: center;
    align-items: center;
`;
